import Button from "@/components/ui/button";
import CustomInput from "@/components/ui/input";
import useNotification from "@/hooks/use-notification";
import { authApi } from "@/core/auth/services/auth.api";
import { setUserFromToken } from "@/store";
import { ROUTES } from "@/constants/auth";
import { t } from "i18next";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../styles/login.module.scss";
import Email from "@/components/icons/email";
import { Lock } from "@/components/icons";

interface StaffLoginProps {
  onLoginSuccess?: (tokenPayload: any) => void;
  domainID?: string;
}

type FieldErrors = Partial<Record<"username" | "password", string>>;

/** Çalışan girişi - Yönetici ile aynı LDAP endpoint kullanır */
const StaffLogin: React.FC<StaffLoginProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { notifyError, notifySuccess } = useNotification();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const usernameEmpty = !form.username.trim();
    const passwordEmpty = !form.password.trim();
    if (usernameEmpty || passwordEmpty) {
      const errors: FieldErrors = {};
      if (usernameEmpty) errors.username = t("login.pleaseEnterUsername");
      if (passwordEmpty) errors.password = t("login.pleaseEnterPassword");
      setFieldErrors(errors);
      const firstMessage = errors.username || errors.password;
      if (firstMessage) notifyError(firstMessage);
      return;
    }

    setFieldErrors({});
    setIsLoading(true);

    try {
      const response = await authApi.login({
        username: form.username.trim(),
        password: form.password,
      });

      setUserFromToken(response, false);
      await new Promise((resolve) => setTimeout(resolve, 100));

      notifySuccess(t("login.loginSuccess"));
      if (onLoginSuccess) {
        onLoginSuccess(response);
      } else {
        navigate(ROUTES.DASHBOARD_USER);
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        (err?.code === "ERR_NETWORK" || !err?.response
          ? t("staffLogin.serverUnreachable")
          : t("login.loginFailed"));
      setFieldErrors({
        username: t("login.invalidUsername"),
        password: t("login.invalidPassword"),
      });
      notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formFields = [
    {
      label: t("login.username"),
      name: "username",
      placeholder: t("login.usernamePlaceholder"),
      type: "text",
      maxLength: 255,
      variant: "standard" as const,
      startIcon: <Email width={24} height={24} />,
    },
    {
      label: t("login.password"),
      name: "password",
      placeholder: "**********",
      type: "password",
      maxLength: 512,
      variant: "standard" as const,
      startIcon: <Lock width={24} height={24} />,
    },
  ];

  const handleInputChange =
    (field: "username" | "password") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  return (
    <form onSubmit={handleSubmit} className={styles["staff-login"]}>
      {formFields.map(
        ({ label, name, placeholder, type, variant, startIcon, maxLength }) => {
          const errorMessage = fieldErrors[name as keyof FieldErrors];
          return (
            <CustomInput
              key={name}
              error={!!errorMessage}
              errorMessage={errorMessage}
              label={label}
              onChange={handleInputChange(name as "username" | "password")}
              placeholder={placeholder}
              type={type}
              maxLength={maxLength}
              value={form[name as keyof typeof form]}
              variant={variant}
              startIcon={startIcon}
            />
          );
        },
      )}
      <Button
        color="primary"
        size="medium"
        style={{ marginTop: "2rem", width: "100%" }}
        type="submit"
        variant="contained"
        loading={isLoading}
      >
        {t("buttons.login")}
      </Button>
    </form>
  );
};

export default StaffLogin;
